import { ParticipationStatus } from '@app/api';
import { ChipColor } from '@app/core';
import { getColorForParticipationStatus } from 'lib/participationUtils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Tab {
  label: string;
  statusList: ParticipationStatus[];
  color: ChipColor;
  adminOnly?: boolean;
  visibleOnDataOnly?: boolean;
  visibleOnGraph?: boolean;
}

export const useUnmoderatedTabs = (
  canSeeQuotas?: boolean,
): Record<string, Tab> => {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      completed: {
        label: t('sessions.details.unmoderatedCategories.completed'),
        statusList: [
          ParticipationStatus.PARTICIPATED,
          ParticipationStatus.PAYMENT_REQUESTED,
          ParticipationStatus.PROCESSING_PAYMENT,
          ParticipationStatus.PAYED,
        ],
        color: getColorForParticipationStatus(ParticipationStatus.PARTICIPATED),
        visibleOnGraph: true,
      },
      started: {
        label: t('sessions.details.unmoderatedCategories.started'),
        statusList: [ParticipationStatus.IN_PROGRESS],
        color: getColorForParticipationStatus(ParticipationStatus.IN_PROGRESS),
        visibleOnGraph: true,
      },
      approved: {
        label: t('sessions.details.unmoderatedCategories.approved'),
        statusList: [ParticipationStatus.ACCEPTED],
        color: getColorForParticipationStatus(ParticipationStatus.ACCEPTED),
        visibleOnGraph: true,
      },
      rejected: {
        label: t('sessions.details.unmoderatedCategories.rejected'),
        statusList: [ParticipationStatus.DECLINED],
        color: getColorForParticipationStatus(ParticipationStatus.DECLINED),
        visibleOnGraph: true,
      },
      outOfQuota: {
        label: t('sessions.details.unmoderatedCategories.outOfQuota'),
        statusList: [ParticipationStatus.OUT_OF_QUOTA],
        color: getColorForParticipationStatus(ParticipationStatus.OUT_OF_QUOTA),
        visibleOnGraph: true,
        adminOnly: !canSeeQuotas,
      },
      gaveUp: {
        label: t('sessions.details.unmoderatedCategories.gaveUp'),
        statusList: [ParticipationStatus.CANCELED],
        color: getColorForParticipationStatus(ParticipationStatus.CANCELED),
      },
      invited: {
        label: t('sessions.details.unmoderatedCategories.invited'),
        statusList: [ParticipationStatus.INVITED],
        visibleOnDataOnly: true,
        color: getColorForParticipationStatus(ParticipationStatus.INVITED),
      },
      other: {
        label: t('sessions.details.unmoderatedCategories.other'),
        adminOnly: true,
        visibleOnDataOnly: true,
        statusList: [
          ParticipationStatus.NOT_INTERESTED,
          ParticipationStatus.APPLIED,
          ParticipationStatus.BOOKED,
          ParticipationStatus.NO_SHOW,
        ],
        color: getColorForParticipationStatus(ParticipationStatus.APPLIED),
      },
    }),
    [t, canSeeQuotas],
  );
};
